import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { namespace } from '@common-config/constants';
import { backendApi } from '@common/reducers/backend-api/backend-api';
import { NewAgentButton } from '@common-components/mascot/NewAgentButton';
import { useAppSelector } from '@common-reducers/hooks/store.hook';
import { botSelectByQuery } from '@common-reducers/DBServiceThunks';
import { ArrowLeft, Loader2 } from 'lucide-react';
import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import classes from '../../common/views/account-view/account-view.module.scss';
import { AgentInspectPanel, AgentRow, muted } from './agent-inspect-shared';
import { AgentWorkspace } from './AgentWorkspace';
import { IntegrationsDirectory } from './IntegrationsDirectory';
import { MCP_DIRECTORY_PATH, MCP_DIRECTORY_SEGMENT } from './McpServersSection';
import { useAgentRowActions } from './useAgentRowActions';
import { agentConversationsPath, agentEditPath, NEW_AGENT_PATH, parseView, WorkspaceView } from './workspace-view';
import { sortAgentsForList } from './agent-list-order';
import { useAgentPageViewed } from '@common/services/use-agent-page-viewed';
import { GuideVideoButton } from '@/common/components/guide-video/guide-video-button';

/** Old `/my-bridge/agents/:agentId` links (bookmarks, open tabs) → `/agents/:agentId`. */
export const LegacyAgentEditRedirect: React.FC = () => {
  const { agentId = '' } = useParams<{ agentId: string }>();
  return <Navigate to={agentEditPath(agentId)} replace />;
};

/**
 * Where `/agents/*` points: the list, or the workspace on a new / existing agent.
 * `/agents` with no agents yet IS the New-agent workspace (the big builder chat).
 */
export type AgentsTarget =
  | { kind: 'list' }
  | { kind: 'directory' }
  | { kind: 'workspace'; agentId: string | null }
  | { kind: 'conversations'; agentId: string };

export function agentsTarget(subPath: string, hasAgents: boolean): AgentsTarget {
  const first = subPath.split('/')[0];
  if (!first) return hasAgents ? { kind: 'list' } : { kind: 'workspace', agentId: null };
  if (first === 'new') return { kind: 'workspace', agentId: null };
  // Agent ids are ObjectIds — never "integrations".
  if (first === MCP_DIRECTORY_SEGMENT) return { kind: 'directory' };
  if (subPath.split('/')[1] === 'conversations') return { kind: 'conversations', agentId: decodeURIComponent(first) };
  return { kind: 'workspace', agentId: decodeURIComponent(first) };
}

/**
 * Agents 2.0 — ONE page for `/agents`, `/agents/new`, `/agents/:agentId` and
 * `/agents/:agentId/conversations`.
 *
 *  - the list (`/agents` with agents): one row per agent; a row opens the agent;
 *  - an agent's conversations (sessions + takeover), on their own page;
 *  - the agent workspace (`/agents/new`, `/agents/:agentId`, and `/agents` while
 *    there are none): the builder chat + the agent form (`AgentWorkspace`).
 *
 * The workspace is rendered at ONE position under ONE route, so going from a new
 * agent to the agent it just created (a URL replace) keeps it mounted — and with
 * it the conversation on screen.
 */
export const MyBridgePage: React.FC = () => {
  const { t, i18n } = useTranslation(namespace);
  // A real open of the web /agents route — a durable, owner-stamped signal, once
  // per mount. NOT the passive /my-bridge/overview poll (see useAgentPageViewed).
  useAgentPageViewed('web_agents_page');
  const isRtl = i18n.dir(i18n.resolvedLanguage || i18n.language) === 'rtl';
  const navigate = useNavigate();
  const { '*': subPath = '' } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  // Same naming rule as the main nav (side-bar-header): "Agents 2.0" only beside
  // the legacy agents, for accounts that have a legacy bot; otherwise "AI Agents".
  const hasLegacyBots = useAppSelector((state) => botSelectByQuery(state.DBBotReducer, {}).length > 0);
  const pageTitle = hasLegacyBots ? t('navigation.agents2') : t('navigation.aiAgents');

  const { data: overview, isLoading, isError, refetch } = backendApi.useGetMyBridgeOverviewQuery();
  // Active first, then most recently updated (sortAgentsForList).
  const agents = useMemo(() => sortAgentsForList(overview?.agents ?? []), [overview?.agents]);
  const target = agentsTarget(subPath, agents.length > 0);
  const rowActions = useAgentRowActions();

  // Created from this page: not "not found" while the overview catches up.
  const createdIds = useRef(new Set<string>());

  const ready = !isLoading && !isError;
  // rev 3.3 §6.17.1: no projects/connections — a provisioned account can build.
  // Not switched on for this account: the same workspace, building off.
  const canCreate = ready && !!overview?.configured && !!overview?.provisioned;
  const activeCount = agents.filter((a) => a.enabled).length;
  const agentId = target.kind === 'list' || target.kind === 'directory' ? null : target.agentId;
  const agent = agentId ? agents.find((a) => a.id === agentId) ?? null : null;

  // Name the tab after the agent; restore the previous title on leave.
  useEffect(() => {
    if (!agent?.label) return;
    const previous = document.title;
    document.title = `${agent.label} · ${pageTitle}`;
    return () => { document.title = previous; };
  }, [agent?.label, pageTitle]);

  const setView = (view: WorkspaceView) => {
    const next = new URLSearchParams(searchParams);
    next.set('view', view);
    setSearchParams(next, { replace: true });
  };

  if (!ready) {
    return (
      <div className={classes.pageContainer}>
        <div className={classes.titleContainer}>
          <h4 className={classes.pageTitle}>{pageTitle}</h4>
        </div>
        <div className={classes.accountSettingBody}>
          {isLoading ? (
            <div className="h-60 flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin" style={muted} /></div>
          ) : (
            <Alert variant="destructive">
              <AlertTitle>{t('myBridge.errorTitle')}</AlertTitle>
              <AlertDescription className="flex flex-col gap-3">
                {t('myBridge.errorDescription')}
                <div><Button variant="outline" size="sm" onClick={() => refetch()}>{t('myBridge.retry')}</Button></div>
              </AlertDescription>
            </Alert>
          )}
        </div>
      </div>
    );
  }

  if (target.kind === 'directory') {
    return <IntegrationsDirectory agents={agents} isRtl={isRtl} backLabel={pageTitle} />;
  }

  if (target.kind !== 'list') {
    if (agentId && !agent && (target.kind === 'conversations' || !createdIds.current.has(agentId))) {
      return (
        <div className={classes.pageContainer}>
          <div className={classes.accountSettingBody}>
            <p className="text-sm py-8" data-testid="agent-edit-not-found">{t('myBridge.form.agentNotFound')}</p>
            <Button variant="outline" size="sm" asChild><Link to="/agents">{pageTitle}</Link></Button>
          </div>
        </div>
      );
    }
  }

  if (target.kind === 'conversations' && agent) {
    return (
      <div className="mx-auto w-full max-w-5xl px-4 sm:px-8 pt-8 sm:pt-10 pb-28" data-testid="agent-conversations-page">
        <div className="flex items-center gap-2 mb-6">
          <Button variant="ghost" size="icon" asChild className="shrink-0">
            <Link to="/agents" aria-label={pageTitle} data-testid="agent-conversations-back">
              <ArrowLeft className={`h-4 w-4 ${isRtl ? 'rotate-180' : ''}`} />
            </Link>
          </Button>
          <h1 className="min-w-0 truncate text-2xl font-semibold tracking-[-0.035em] text-ai-text" dir="auto">
            {t('bridgeInspector.conversations')}
          </h1>
          <Button variant="outline" size="sm" asChild className="ms-auto shrink-0">
            <Link to={agentEditPath(agent.id)}>{t('common.edit')}</Link>
          </Button>
        </div>
        <AgentInspectPanel
          agent={agent}
          bridgeId={overview?.bridge?.id}
          canManage={overview?.isAdmin}
          isOwner
        />
      </div>
    );
  }

  if (target.kind === 'workspace') {
    return (
      <div className="h-full" data-testid="agents-workspace-page">
        <AgentWorkspace
          agentId={agentId}
          view={parseView(searchParams.get('view'))}
          onViewChange={setView}
          onCreated={(id, view) => {
            createdIds.current.add(id);
            navigate(`${agentEditPath(id)}?view=${view}`, { replace: true });
          }}
          isRtl={isRtl}
          headerStart={agents.length > 0 && (
            <>
              <Button variant="ghost" size="icon" asChild className="shrink-0">
                <Link to="/agents" aria-label={pageTitle} data-testid="agent-edit-back">
                  <ArrowLeft className={`h-4 w-4 ${isRtl ? 'rotate-180' : ''}`} />
                </Link>
              </Button>
            </>
          )}
        />
      </div>
    );
  }

  // pb-28: the last row scrolls clear of the Crisp launcher (bottom corner).
  return (
    <div className="mx-auto w-full max-w-5xl px-4 sm:px-8 pt-8 sm:pt-10 pb-28" data-testid="agents-list">
      <div className="flex items-center gap-3 sm:gap-4 mb-6 sm:mb-8">
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-[-0.035em] text-ai-text">
            {pageTitle}
            <GuideVideoButton lesson="agent-use-cases" variant="icon" surface="agents_list_header" style={{ marginInlineStart: 8 }} />
          </h1>
          <p className="text-sm mt-0.5 text-ai-text-secondary">
            {t('myBridge.list.summary', { count: agents.length, active: activeCount })}
          </p>
        </div>
        <Button variant="outline" asChild className="shrink-0">
          <Link to={MCP_DIRECTORY_PATH} data-testid="agents-integrations-link">
            {t('myBridge.mcp.directoryTitle')}
          </Link>
        </Button>
        <NewAgentButton
          label={t('myBridge.list.newAgent')}
          onClick={() => navigate(NEW_AGENT_PATH)}
          disabled={!canCreate}
          className="shrink-0"
          data-testid="create-agent-button"
        />
      </div>

      <div className="rounded-ai-card border border-ai-border bg-ai-surface overflow-hidden">
        <div className="flex flex-col divide-y divide-ai-border px-3 sm:px-5">
          {agents.map((a) => (
            <AgentRow
              key={a.id}
              agent={a}
              bridgeId={overview?.bridge?.id}
              canManage={overview?.isAdmin}
              onOpenConversations={(x) => navigate(agentConversationsPath(x.id))}
              onOpen={(x) => navigate(agentEditPath(x.id))}
              fullBleed
              openHref={agentEditPath(a.id)}
              isOwner
              {...rowActions.rowProps(a)}
            />
          ))}
        </div>
      </div>

      {rowActions.confirmDialog}
    </div>
  );
};
